import React, { useEffect, useState, useRef, useCallback } from "react";
import { getAll } from "../api/API";
import ResourceFilters from "../components/Resources/ResourceFilters";
import ResourceCard from "../components/Resources/ResourceCard";
import "../styles/Resources.css";

const LIMIT = 10;

const ResourcePage = () => {
  const [resources, setResources] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const observerRef = useRef(null);
  const resultsRef = useRef(null);

  // Load resources (paginated)
  const loadMoreResources = async () => {
    if (!hasMore) return;

    const res = await getAll(`/resources?limit=${LIMIT}&page=${page}`);

    if (res.docs.length < LIMIT) setHasMore(false);

    setResources((prev) => [...prev, ...res.docs]);
    setFiltered((prev) => [...prev, ...res.docs]);
  };

  useEffect(() => {
    loadMoreResources();
  }, [page]);

  // Infinite Scroll — detect when last card is visible
  const lastResourceRef = useCallback(
    (node) => {
      if (!hasMore) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [hasMore],
  );

  // Apply filters dynamically
  const applyFilters = () => {
    let data = resources;

    if (selectedCategory) {
      data = data.filter((r) => r.category === selectedCategory);
    }

    if (selectedTags.length) {
      data = data.filter((r) =>
        r.tags.some((t) => selectedTags.includes(t.tag)),
      );
    }

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q),
      );
    }

    setFiltered(data);
  };

  useEffect(() => {
    applyFilters();
  }, [selectedCategory, selectedTags, searchText, resources]);

  const resetFilters = () => {
    setSelectedCategory("");
    setSelectedTags([]);
    setSearchText("");
    setFiltered(resources);
  };

  /* -------------------- MOBILE SCROLL -------------------- */
  const scrollToResults = () => {
    resultsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className="resource-wrapper">
      <div className="row">
        {/* LEFT FILTERS */}
        <div className="col-md-5 resource-left-section">
          <h1 className="wise-secondary-gradient">
            Find the Perfect Learning Resource
          </h1>

          <p className="text-secondary">
            Browse books, videos and literature. Filter by category, tags, or
            search instantly.
          </p>

          <ResourceFilters
            resources={resources}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            resetFilters={resetFilters}
            searchText={searchText}
            setSearchText={setSearchText}
          />

          {/* MOBILE SHOW RESULTS BUTTON */}
          <button
            className=" result-button btn btn-primary w-80 d-md-none mt-3 rounded-2"
            onClick={scrollToResults}
          >
            Show results
          </button>
        </div>

        {/* RIGHT RESOURCE LIST */}
        <div className="col-md-7 right-section" ref={resultsRef}>
          <div className="card-column">
            {filtered.length === 0 ? (
              <p className="text-center text-secondary py-4">
                No resources were found.
              </p>
            ) : (
              <>
                {filtered.map((item, index) => {
                  if (index === filtered.length - 1) {
                    return (
                      <div ref={lastResourceRef} key={item.id}>
                        <ResourceCard item={item} />
                      </div>
                    );
                  }
                  return <ResourceCard key={item.id} item={item} />;
                })}

                {!hasMore && (
                  <p className="text-center text-secondary py-3">
                    End of resources.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcePage;
